"use client";

import type * as React from "react";
import { Command, CommandGroup, CommandItem, CommandList } from "@/components/ui/command";

interface PropertyPickerListContentProps<T> {
  options: Array<{
    value: T;
    label: string;
    icon?: React.ReactNode;
    displayLabel?: React.ReactNode;
    hidden?: boolean;
  }>;
  groups?: Array<{
    label: string;
    options: Array<{
      value: T;
      label: string;
      icon?: React.ReactNode;
      displayLabel?: React.ReactNode;
    }>;
  }>;
  renderItem?: (
    option: { value: T; label: string; icon?: React.ReactNode; displayLabel?: React.ReactNode },
    isSelected: boolean,
  ) => React.ReactNode;
  onSelect?: (value: T) => void;
  selectedValue?: T | null;
}

function DefaultListItem({ label, icon }: { label: React.ReactNode; icon?: React.ReactNode }) {
  return (
    <>
      {icon}
      <span className="truncate">{label}</span>
    </>
  );
}

export function PropertyPickerListContent<T>({
  options,
  groups,
  renderItem,
  onSelect,
  selectedValue,
}: PropertyPickerListContentProps<T>) {
  const handleSelect = (value: T) => {
    onSelect?.(value);
  };

  if (groups && groups.length > 0) {
    return (
      <Command>
        <CommandList>
          {groups.map((group) => (
            <CommandGroup key={group.label} heading={group.label}>
              {group.options.map((option) => (
                <CommandItem
                  key={String(option.value)}
                  value={option.label}
                  onSelect={() => handleSelect(option.value)}
                  data-checked={option.value === selectedValue ? "" : undefined}
                >
                  {renderItem ? (
                    renderItem(option, option.value === selectedValue)
                  ) : (
                    <DefaultListItem label={option.displayLabel ?? option.label} icon={option.icon} />
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          ))}
        </CommandList>
      </Command>
    );
  }

  return (
    <Command>
      <CommandList>
        <CommandGroup>
          {options
            .filter((option) => !option.hidden)
            .map((option) => (
              <CommandItem
                key={String(option.value)}
                value={option.label}
                onSelect={() => handleSelect(option.value)}
                data-checked={option.value === selectedValue ? "" : undefined}
              >
                {renderItem ? (
                  renderItem(option, option.value === selectedValue)
                ) : (
                  <DefaultListItem label={option.displayLabel ?? option.label} icon={option.icon} />
                )}
              </CommandItem>
            ))}
        </CommandGroup>
      </CommandList>
    </Command>
  );
}
